// src/pages/ExamWait.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';

function useQuery(){ const { search } = useLocation(); return useMemo(()=>new URLSearchParams(search),[search]); }

export default function ExamWait(){
    const { id } = useParams(); // attemptId
    const nav = useNavigate();
    const q = useQuery();

    const [st, setSt] = useState(q.get('st') || null);
    const [pre, setPre] = useState(null); // { server_now, start_at, duration_min, proctoring }
    const [err, setErr] = useState('');

    // Zgody
    const [consentCam, setConsentCam] = useState(false);
    const [consentMic, setConsentMic] = useState(false);

    // SEB detection (backend)
    const [isSeb, setIsSeb] = useState(false);
    const [sebChecked, setSebChecked] = useState(false);

    // ====== KAMERA ======
    const [previewOn, setPreviewOn] = useState(false);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState('');
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // ====== MIKROFON ======
    const [micOn, setMicOn] = useState(false);
    const [micDevices, setMicDevices] = useState([]);
    const [micId, setMicId] = useState('');
    const [micErr, setMicErr] = useState('');
    const micStreamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const srcNodeRef = useRef(null);
    const rafRef = useRef(null);
    const meterCanvasRef = useRef(null);

    // --- SEB check on mount ---
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const resp = API.sebCheck
                    ? await API.sebCheck()
                    : await fetch('/api/seb/check', { credentials: 'include' }).then(r => r.json());
                if (mounted) setIsSeb(!!resp?.isSEB);
            } catch {
                if (mounted) setIsSeb(false);
            } finally {
                if (mounted) setSebChecked(true);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Reset wyboru urządzeń dla nowego attemptu.
    useEffect(()=>{
        const last = localStorage.getItem('exam.lastAttemptId');
        if (last !== String(id)) {
            localStorage.removeItem('exam.cameraId');
            localStorage.removeItem('exam.micId');
            localStorage.setItem('exam.lastAttemptId', String(id));
        }
        const savedCam = localStorage.getItem('exam.cameraId');
        if (savedCam) setDeviceId(savedCam);
        const savedMic = localStorage.getItem('exam.micId');
        if (savedMic) setMicId(savedMic);
    }, [id]);

    // Jeśli brak st – pobierz i dopisz do URL
    useEffect(()=>{
        let canceled=false;
        (async()=>{
            if (st) return;
            try{
                const { token } = await API.getSession(id);
                if (canceled) return;
                setSt(token);
                const url = new URL(window.location.href);
                url.searchParams.set('st', token);
                window.history.replaceState({}, '', url.toString());
            }catch(e){ setErr(e.message||'Błąd przygotowania sesji'); }
        })();
        return ()=>{ canceled=true; };
    }, [id, st]);

    // Preflight + odświeżanie co 60s
    useEffect(()=>{
        let timer;
        (async()=>{
            if (!st) return;
            try{
                const p = await API.preflight(id, st);
                setPre(p);
            }catch(e){ setErr(e.message||'Błąd preflight'); }
            timer = setInterval(async ()=>{
                try{ const p = await API.preflight(id, st); setPre(p); }catch{}
            }, 60000);
        })();
        return ()=>clearInterval(timer);
    }, [id, st]);

    // Odliczanie wg czasu serwera
    const [now, setNow] = useState(Date.now());
    useEffect(()=>{ const t = setInterval(()=>setNow(Date.now()), 1000); return ()=>clearInterval(t); }, []);
    const offsetMs = useMemo(()=> pre?.server_now ? (new Date(pre.server_now).getTime() - Date.now()) : 0, [pre?.server_now]);
    const leftMs   = useMemo(()=> pre?.start_at ? (new Date(pre.start_at).getTime() - (now + offsetMs)) : null, [pre?.start_at, now, offsetMs]);
    const leftStr  = useMemo(()=>{
        if (leftMs==null) return '—';
        const s = Math.max(0, Math.ceil(leftMs/1000));
        const hh = Math.floor(s/3600), mm = Math.floor((s%3600)/60), ss = s%60;
        return `${hh>0?hh+':':''}${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
    }, [leftMs]);
    const preflightReady = !!pre;
    const proctoringEnabled = pre?.proctoring === true;

    // ====== CAMERA helpers ======
    function pickValidDeviceId(savedId, vids) {
        const ids = new Set(vids.map(d => d.deviceId));
        return ids.has(savedId) ? savedId : (vids[0]?.deviceId || '');
    }
    function stopPreview(keepState=false){
        try { streamRef.current?.getTracks()?.forEach(t=>t.stop()); } catch {}
        if (videoRef.current) videoRef.current.srcObject = null;
        streamRef.current = null;
        if (!keepState) setPreviewOn(false);
    }
    async function startPreview(auto=false) {
        try{
            if (!navigator.mediaDevices?.getUserMedia) {
                if (!auto) setErr('Przeglądarka nie obsługuje kamery (getUserMedia).');
                return;
            }
            const host = window.location.hostname;
            const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
            if (!window.isSecureContext && !isLocalhost) {
                if (!auto) setErr('Wymagany jest HTTPS (poza localhost) do użycia kamery.');
                return;
            }
            const all = await navigator.mediaDevices.enumerateDevices();
            const vids = all.filter(d => d.kind === 'videoinput');
            setDevices(vids);

            const saved = localStorage.getItem('exam.cameraId') || deviceId;
            const chosenId = pickValidDeviceId(saved, vids);
            if (chosenId !== deviceId) setDeviceId(chosenId);

            stopPreview(true);

            const base = { width: { ideal: 1280 }, height: { ideal: 720 } };
            let constraints = chosenId
                ? { video: { ...base, deviceId: { exact: chosenId } }, audio:false }
                : { video: { ...base, facingMode: { ideal: 'user' } }, audio:false };

            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            } catch (e) {
                if (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError') {
                    localStorage.removeItem('exam.cameraId');
                    setDeviceId('');
                    constraints = { video: { ...base, facingMode: { ideal: 'user' } }, audio:false };
                    stream = await navigator.mediaDevices.getUserMedia(constraints);
                } else {
                    throw e;
                }
            }

            streamRef.current = stream;

            const v = videoRef.current;
            if (v) {
                v.srcObject = stream;
                v.muted = true;
                v.playsInline = true;
                await new Promise((resolve) => {
                    const ready = () => {
                        v.removeEventListener('loadedmetadata', ready);
                        const p = v.play();
                        if (p && typeof p.then === 'function') p.then(()=>resolve()).catch(()=>resolve());
                        else resolve();
                    };
                    if (v.readyState >= 1) ready(); else v.addEventListener('loadedmetadata', ready, { once:true });
                });
            }

            const usedId = stream.getVideoTracks()[0]?.getSettings?.().deviceId || chosenId || '';
            if (usedId) {
                localStorage.setItem('exam.cameraId', usedId);
                if (usedId !== deviceId) setDeviceId(usedId);
            }

            setPreviewOn(true);
            if (!auto) setErr('');
        }catch(e){
            if (!auto) {
                const name = e?.name || 'Error';
                const message = e?.message || '';
                setErr(`Brak dostępu do kamery (${name}: ${message}).`);
            }
            setPreviewOn(false);
        }
    }
    function rememberCamera() {
        let idToSave = deviceId;
        if (!idToSave && streamRef.current) {
            const track = streamRef.current.getVideoTracks()[0];
            idToSave = track?.getSettings?.().deviceId || '';
        }
        if (idToSave) localStorage.setItem('exam.cameraId', idToSave);
    }

    // Auto-start podglądu kamery
    useEffect(()=>{
        if (!proctoringEnabled) {
            stopPreview();
            return;
        }
        (async ()=>{ await startPreview(true); })();
        /* eslint-disable-next-line */
    }, [id, proctoringEnabled]);
    // Restart przy zmianie kamery
    useEffect(()=>{ if (proctoringEnabled && previewOn) { (async ()=>{ await startPreview(true); })(); } /* eslint-disable-next-line */ }, [deviceId]);

    // ====== MIC helpers ======
    function stopMic(keep=false){
        try { micStreamRef.current?.getTracks()?.forEach(t=>t.stop()); } catch {}
        micStreamRef.current = null;
        try{ rafRef.current && cancelAnimationFrame(rafRef.current); }catch{}
        rafRef.current = null;
        try{ srcNodeRef.current && srcNodeRef.current.disconnect(); }catch{}
        try{ analyserRef.current && analyserRef.current.disconnect(); }catch{}
        try{ audioCtxRef.current && audioCtxRef.current.close(); }catch{}
        srcNodeRef.current = null;
        analyserRef.current = null;
        audioCtxRef.current = null;
        if (!keep) setMicOn(false);
    }

    function drawMeter(){
        const cvs = meterCanvasRef.current;
        const an = analyserRef.current;
        if (!cvs || !an) return;
        const ctx = cvs.getContext('2d');
        const data = new Uint8Array(an.frequencyBinCount);
        const W = cvs.width, H = cvs.height;

        const loop = ()=>{
            rafRef.current = requestAnimationFrame(loop);
            an.getByteTimeDomainData(data);

            let sum = 0;
            for (let i=0;i<data.length;i++){
                const v = (data[i]-128)/128;
                sum += v*v;
            }
            const rms = Math.sqrt(sum/data.length);
            const bar = Math.min(1, rms * 3); // prosty przelicznik

            ctx.clearRect(0,0,W,H);
            ctx.fillStyle = '#f3f3f3'; ctx.fillRect(0,0,W,H);
            ctx.fillStyle = '#ddd'; ctx.fillRect(0,H-12,W,12);
            ctx.fillStyle = '#2a8'; ctx.fillRect(0,H-12,Math.floor(W*bar),12);
            ctx.fillStyle = '#e33'; ctx.fillRect(Math.floor(W*bar)-2,H-14,4,16);
        };
        loop();
    }

    function pickValidMicId(savedId, list){
        const ids = new Set(list.map(d => d.deviceId));
        return ids.has(savedId) ? savedId : (list[0]?.deviceId || '');
    }

    async function startMic(auto=false){
        try{
            setMicErr('');
            if (!navigator.mediaDevices?.getUserMedia) {
                if (!auto) setMicErr('Przeglądarka nie obsługuje mikrofonu (getUserMedia).');
                return;
            }
            const host = window.location.hostname;
            const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
            if (!window.isSecureContext && !isLocalhost) {
                if (!auto) setMicErr('Wymagany jest HTTPS (poza localhost) do użycia mikrofonu.');
                return;
            }

            const all = await navigator.mediaDevices.enumerateDevices();
            const auds = all.filter(d => d.kind === 'audioinput');
            setMicDevices(auds);

            const saved = localStorage.getItem('exam.micId') || micId;
            const chosenId = pickValidMicId(saved, auds);
            if (chosenId !== micId) setMicId(chosenId);

            stopMic(true);

            let constraints = chosenId
                ? { audio: { deviceId: { exact: chosenId }, echoCancellation:true, noiseSuppression:true, autoGainControl:true } }
                : { audio: { echoCancellation:true, noiseSuppression:true, autoGainControl:true } };

            let stream;
            try{
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            }catch(e){
                if (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError'){
                    localStorage.removeItem('exam.micId');
                    setMicId('');
                    constraints = { audio: { echoCancellation:true, noiseSuppression:true, autoGainControl:true } };
                    stream = await navigator.mediaDevices.getUserMedia(constraints);
                }else{
                    throw e;
                }
            }

            micStreamRef.current = stream;

            // Audio graph
            try{
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                audioCtxRef.current = ctx;
                const src = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 1024;
                src.connect(analyser);
                analyserRef.current = analyser;
                srcNodeRef.current = src;
                drawMeter();
            }catch{
                // brak wizualizacji ≠ blokada
            }

            const usedId = stream.getAudioTracks()[0]?.getSettings?.().deviceId || chosenId || '';
            if (usedId){
                localStorage.setItem('exam.micId', usedId);
                if (usedId !== micId) setMicId(usedId);
            }

            setMicOn(true);
        }catch(e){
            if (!auto){
                setMicErr(`Brak dostępu do mikrofonu (${e?.name||'Error'}: ${e?.message||''}).`);
            }
            setMicOn(false);
        }
    }

    // Auto-start (spróbuj delikatnie)
    useEffect(()=>{
        if (!proctoringEnabled) {
            stopMic();
            return;
        }
        (async ()=>{ await startMic(true); })();
        /* eslint-disable-next-line */
    }, [id, proctoringEnabled]);
    // Restart przy zmianie wyboru
    useEffect(()=>{ if (proctoringEnabled && micOn){ (async ()=>{ await startMic(true); })(); } /* eslint-disable-next-line */ }, [micId]);

    function rememberMic(){
        let idToSave = micId;
        if (!idToSave && micStreamRef.current){
            const track = micStreamRef.current.getAudioTracks()[0];
            idToSave = track?.getSettings?.().deviceId || '';
        }
        if (idToSave) localStorage.setItem('exam.micId', idToSave);
    }

    // Przejście do weryfikacji (po czasie startu)
    async function goToVerify() {
        if (!st) return setErr('Brak tokenu st');
        if (leftMs > 0) return;
        try{
            rememberCamera();
            rememberMic();
            stopPreview();
            stopMic();

            const client = isSeb ? 'seb' : 'web';

            try {
                await API.enterSession(id, { st, client });
            } catch (e) {
                const msg = String(e?.message || '');
                if (msg.includes('session_active')) {
                    const ok = window.confirm('Egzamin jest już otwarty w innym oknie/urządzeniu. Przejąć sesję?');
                    if (!ok) return;
                    await API.enterSession(id, { st, client, takeover:true });
                } else if (msg.includes('not_yet')) {
                    return setErr('Egzamin jeszcze się nie rozpoczął — odśwież odliczanie.');
                } else {
                    throw e;
                }
            }

            nav(proctoringEnabled ? `/student/egzamin/${id}/weryfikacja` : `/student/egzamin/${id}/trwa`);
        }catch(e){ setErr(e.message||'Błąd wejścia do weryfikacji'); }
    }

    const startAt = pre?.start_at ? new Date(pre.start_at).toLocaleString() : '—';

    return (
        <div style={{ maxWidth: 800, margin:'0 16px', display:'grid', gap:16, marginTop:80, marginBottom: 50 }}>
            <h1>Poczekalnia egzaminu</h1>

            <div style={{ display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
                <div><b>Start o:</b> {startAt}</div>
                <div data-tour="countdown"><b>Do rozpoczęcia:</b> {leftStr}</div>
                <div><b>Czas trwania:</b> {pre?.duration_min ?? '—'} min</div>
                {sebChecked && (
                    <div style={{ marginLeft: 'auto', opacity: 0.7, fontSize: 12 }}>
                        Tryb: {isSeb ? 'SEB' : 'przeglądarka'}
                    </div>
                )}
            </div>

                <div style={{ border:'2px solid #0a66', background:'linear-gradient(to bottom, #0f172a 0 0%, #003671 100% 100%)', borderRadius:14, padding:16 }}><div style={{fontSize: 18, color:'white', lineHeight:'1.5' }}>
                {!preflightReady
                    ? 'Przygotowuję poczekalnię egzaminu...'
                    : proctoringEnabled
                    ? 'Egzamin jest nagrywany i automatycznie analizowany pod kątem naruszeń zasad. Zabronione jest wychodzenie z trybu pełnoekranowego. Wszelkie próby naruszeń są rejestrowane i mogą spowodować unieważnienie egzaminu.'
                    : 'Proctoring wideo jest wyłączony dla tego terminu. Kamera, mikrofon i weryfikacja twarzy nie są wymagane. Aktywność w trybie egzaminu nadal jest rejestrowana jako lista zdarzeń i wpływa na stopień zaufania.'}
                </div>
            </div>

            {/* TEST KAMERY */}
            {proctoringEnabled && <div  data-tour="camera"  style={{ border:'1px solid #eee', borderRadius:8, padding:12, backgroundColor:'white' }}>
                <h3>Test kamery</h3>
                {!previewOn ? (
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <button onClick={()=>startPreview(false)}>Włącz kamerę</button>
                        <small style={{ opacity:0.8 }}>
                            Wymagany HTTPS (poza <code>localhost</code>) i zgoda przeglądarki.
                        </small>
                    </div>
                ) : (
                    <>
                        <div style={{ margin:'8px 0', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <label>Kamera:&nbsp;</label>
                            <select value={deviceId} onChange={e=>setDeviceId(e.target.value)}>
                                {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Kamera'}</option>)}
                            </select>
                            <button className={'but-smal'} onClick={async()=>{ await startPreview(false); }}>Przeładuj</button>
                            <button className={'but-smal'}
                                onClick={()=>{
                                    localStorage.removeItem('exam.cameraId');
                                    setDeviceId('');
                                    startPreview(false);
                                }}
                            >
                                Reset wyboru
                            </button>
                            <button className={'but-smal'} onClick={()=>stopPreview(false)}>Wyłącz</button>
                        </div>
                        <div style={{ width:'100%', aspectRatio:'4/3', background:'#000', borderRadius:8, overflow:'hidden' }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{ width:'100%', height:'100%', objectFit:'cover', transform:'scaleX(-1)' }}
                            />
                        </div>
                    </>
                )}
                <div data-tour="consents-camera" style={{ marginTop:20 }}>
                    <label>
                        <input type="checkbox" checked={consentCam} onChange={e=>setConsentCam(e.target.checked)} />
                        {' '}Wyrażam zgodę na użycie kamery podczas egzaminu.
                    </label>
                </div>
            </div>}
            {proctoringEnabled && <hr />}

            {/* TEST MIKROFONU */}
            {proctoringEnabled && <div data-tour="micro" style={{ border:'1px solid #eee', borderRadius:8, padding:12, marginTop:20, backgroundColor:'white' }}>
                <h3>Test mikrofonu</h3>
                {!micOn ? (
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <button className={'but-smal'} onClick={()=>startMic(false)}>Włącz mikrofon</button>
                        <small style={{ opacity:0.8 }}>
                            Jeśli lista jest pusta — nadaj uprawnienia do mikrofonu w przeglądarce.
                        </small>
                    </div>
                ) : (
                    <>
                        <div style={{ margin:'8px 0', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <label>Mikrofon:&nbsp;</label>
                            <select value={micId} onChange={e=>setMicId(e.target.value)}>
                                {micDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Mikrofon'}</option>)}
                            </select>
                            <button className={'but-smal'} onClick={async()=>{ await startMic(false); }}>Przeładuj</button>
                            <button className={'but-smal'}
                                onClick={()=>{
                                    localStorage.removeItem('exam.micId');
                                    setMicId('');
                                    startMic(false);
                                }}
                            >
                                Reset wyboru
                            </button>
                            <button className={'but-smal'} onClick={()=>stopMic(false)}>Wyłącz</button>
                        </div>
                        <div style={{ display:'grid', gap:8 }}>
                            <div style={{ fontSize:13, opacity:0.8, marginTop:10 }}>Mów do mikrofonu — pasek powinien reagować:</div>
                            <canvas
                                ref={meterCanvasRef}
                                width={360}
                                height={32}
                                style={{ width:360, height:32, borderRadius:6, background:'#f3f3f3' }}
                            />
                        </div>
                    </>
                )}
                {!!micErr && <div style={{ color:'#a00', marginTop:8 }}>{micErr}</div>}
                <div data-tour="consents-micro" style={{ marginTop:20 }}>
                    <label>
                        <input type="checkbox" checked={consentMic} onChange={e=>setConsentMic(e.target.checked)} />
                        {' '}Wyrażam zgodę na użycie mikrofonu podczas egzaminu.
                    </label>
                </div>
            </div>}

            <div data-tour="weryfikacja" style={{ display:'flex', gap:8 }}>
                <button className={'but-cta'}
                    onClick={goToVerify}
                    disabled={!preflightReady || (proctoringEnabled && (!consentCam || !consentMic)) || leftMs>0}
                    title={proctoringEnabled && (!consentCam || !consentMic) ? 'Zaznacz wymagane zgody' : undefined}
                >
                    {leftMs>0 ? 'Czekaj do startu…' : (proctoringEnabled ? 'Przejdź do weryfikacji' : 'Rozpocznij egzamin')}
                </button>
            </div>

            {err && <div style={{ color:'#a00' }}>{err}</div>}
        </div>
    );
}
